import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider.js';
import { getTenantCode } from '../auth/authApi.js';
import { usePublicBranding, portalName } from '../theme/usePublicBranding.js';
import { LanguageSwitcher } from '../../../shared/i18n/LanguageSwitcher.js';

export interface VendorTopbarProps {
  readonly env: string;
  readonly onToggleSidebar: () => void;
}

export function VendorTopbar({ env, onToggleSidebar }: VendorTopbarProps): React.ReactElement {
  const { user, logout } = useAuth();
  const [open, setOpen] = React.useState(false);
  const branding = usePublicBranding(getTenantCode());
  return (
    <header
      role="banner"
      style={{
        gridArea: 'topbar',
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        padding: '0 16px',
        background: 'var(--color-bg-elevated)',
        borderBottom: '1px solid var(--color-border-default)',
        height: 56,
      }}
    >
      <button
        type="button"
        aria-label="Toggle navigation"
        data-testid="vendor-topbar-toggle"
        onClick={onToggleSidebar}
        style={{ background: 'transparent', border: 'none', fontSize: 20, cursor: 'pointer' }}
      >
        ☰
      </button>
      <Link to="/vendor/dashboard" data-testid="vendor-topbar-logo" style={{ fontWeight: 700, color: 'var(--color-brand-primary)', textDecoration: 'none' }}>
        {portalName(branding)}
      </Link>
      <span
        data-testid="vendor-topbar-env"
        style={{
          background: 'var(--color-warning-bg)',
          color: 'var(--color-warning)',
          padding: '2px 8px',
          borderRadius: 4,
          fontSize: 'var(--font-size-xs)',
          fontFamily: 'var(--font-family-mono)',
        }}
      >
        {env}
      </span>
      <div style={{ flex: 1 }} />
      <LanguageSwitcher testId="vendor-language-switcher" />
      <div style={{ position: 'relative' }}>
        <button
          type="button"
          data-testid="vendor-topbar-profile"
          aria-haspopup="menu"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          style={{ background: 'transparent', border: '1px solid var(--color-border-default)', borderRadius: 4, padding: '4px 8px' }}
        >
          {user?.displayName ?? 'Guest'}
        </button>
        {open && (
          <div
            role="menu"
            style={{
              position: 'absolute',
              right: 0,
              top: 'calc(100% + 4px)',
              background: 'var(--color-bg-elevated)',
              border: '1px solid var(--color-border-default)',
              borderRadius: 4,
              minWidth: 160,
              padding: 4,
              zIndex: 100,
            }}
          >
            <button
              type="button"
              role="menuitem"
              data-testid="vendor-sign-out"
              onClick={() => {
                logout();
                setOpen(false);
              }}
              style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px', background: 'transparent', border: 'none', cursor: 'pointer' }}
            >
              Sign out
            </button>
          </div>
        )}
      </div>
    </header>
  );
}
